import { useMemo } from 'react';
import { usePermission } from '@/hooks/usePermission';
import { useAuthStore } from '@/stores/authStore';
import { selectFolderRows, useConversationStore } from '@/stores/conversationStore';
import type { FolderId } from '@/types';
import { folderMatcher } from './folders';

/**
 * How many conversations are waiting for a reply draft review (the DRAFTS folder, OP-582): the rows that carry a pending
 * draft and are not blocked (a spam tombstone or the hidden set). Derived from the row cache, not from the counts
 * endpoint (which does not know drafts): the drafts feed fills the cache at boot / reconnect / the 60 s poll and the
 * live draft pushes keep it current between reads, so this number moves the moment a draft arrives or is resolved.
 */
export function useDraftCount(): number {
  const conversations = useConversationStore((s) => s.conversations);
  const spamTombstones = useConversationStore((s) => s.spamTombstones);
  const hiddenSessions = useConversationStore((s) => s.hiddenSessions);
  const { roles } = usePermission();
  const me = useAuthStore((s) => s.user?.agentId);
  // The SAME rule the drafts folder lists by, so the badge and the list can never disagree about a row.
  return useMemo(
    () =>
      selectFolderRows(
        { conversations, folderPages: {}, spamTombstones, hiddenSessions },
        'drafts',
        folderMatcher('drafts', { me, roles }),
      ).length,
    [conversations, spamTombstones, hiddenSessions, me, roles],
  );
}

/**
 * The number each folder shows (rail badges, list header, the rail's nav badges).
 *
 *  - every list folder: the SERVER's count (`GET /api/sessions/counts`), never the number of rows loaded so far - a
 *    folder is paged, so the rows in memory are a lower bound at best. A key the server did not send is 0 (it only sends
 *    `cat:<key>` / `tag:<key>` when there is something in them);
 *  - `drafts`: {@link useDraftCount}, always a number;
 *  - before the first counts answer: `null` ("not known yet" - the caller draws a dash, never a false 0).
 */
export function useFolderCounts(): { countOf: (id: FolderId) => number | null; loaded: boolean } {
  const folderCounts = useConversationStore((s) => s.folderCounts);
  const loaded = useConversationStore((s) => s.countsLoaded);
  const drafts = useDraftCount();
  return useMemo(
    () => ({
      loaded,
      countOf: (id: FolderId): number | null => {
        if (id === 'drafts') return drafts;
        if (!loaded) return null;
        const n = folderCounts[id];
        return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
      },
    }),
    [folderCounts, loaded, drafts],
  );
}
